import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { MOBILE_TOP_INSET, useDesktopLayout } from "./layout/AppLayout";
import { isTelegramMiniApp } from "../utils/platform";
import { navGradientLineSx, navSurfaceSx } from "../styles/tokens";

// Порог прокрутки, после которого у блока появляется фон
const SCROLLED_THRESHOLD = 8;

// Ближайший прокручиваемый родитель (на десктопе и в PWA прокручивается контейнер AppLayout, а не окно)
export function getScrollParent(node) {
  let parent = node?.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

// onHeight — высота закреплённого блока на телефоне (px): под ним прилипают свои заголовки экрана
// (группы «Склада»); на десктопе блок не закреплён — 0
const StickyFiltersBar = ({ children, onHeight }) => {
  const desktop = useDesktopLayout();
  const barRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!onHeight) return undefined;
    const node = barRef.current;
    if (desktop || !node || typeof ResizeObserver === "undefined") {
      onHeight(0);
      return undefined;
    }
    const observer = new ResizeObserver(() => onHeight(node.offsetHeight));
    observer.observe(node);
    return () => observer.disconnect();
  }, [desktop, onHeight]);

  useEffect(() => {
    if (desktop) {
      return undefined;
    }
    // Прокручиваться может и окно, и контейнер AppLayout — слушаем оба (passive)
    const scroller = getScrollParent(barRef.current);
    const onScroll = () => {
      const y = Math.max(window.scrollY || 0, scroller ? scroller.scrollTop : 0);
      setScrolled(y > SCROLLED_THRESHOLD);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    scroller?.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      scroller?.removeEventListener("scroll", onScroll);
    };
  }, [desktop]);

  if (desktop) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        {children}
      </Box>
    );
  }

  const stickyTop = MOBILE_TOP_INSET;
  // В покое прозрачно — под блоком виден фон страницы; при прокрутке — поверхность как у шапки
  const surfaceSx = scrolled ? navSurfaceSx(isTelegramMiniApp()) : { backgroundColor: "transparent" };
  const transitionSx = {
    transition: "background-color 150ms ease",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  };

  return (
    <Box
      ref={barRef}
      sx={{
        position: "sticky",
        top: stickyTop,
        zIndex: 4,
        width: "calc(100% + 32px)",
        mx: "-16px",
        mb: 2,
        px: 2,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1.2,
        ...surfaceSx,
        ...transitionSx,
        // Зона над блоком (статус-бар / safe-area): та же поверхность, что и у блока
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          right: 0,
          bottom: "100%",
          height: stickyTop,
          ...surfaceSx,
          ...transitionSx,
        },
        // Градиентная линия снизу — только в состоянии «прокручено»
        ...(scrolled && { "&::after": navGradientLineSx("bottom") }),
      }}
    >
      {children}
    </Box>
  );
};

export default StickyFiltersBar;
